// SDK › Signals (D111, APS-358; the mock's Signals page, design/console-d/views/capture.js):
// every device captures every signal family; this page sets, per app, what leaves the device
// (never, on a trigger, sampled, always), the triggers and the window before them, how long the
// device keeps what it holds and how much, and lists the requests sent to one device or one app
// user. Members read; owners and admins publish, and the API audits each change. The mock's
// volumes, rules canvas and templates have no API yet, so they are not drawn.
import { useQuery } from '@tanstack/react-query'
import { Link, useSearch } from '@tanstack/react-router'
import { type FormEvent, useEffect, useId, useState } from 'react'
import { isApiError } from '../api/client'
import {
  appsQuery,
  deviceRequestsQuery,
  instrumentationQuery,
  orgQuery,
  projectsQuery,
  ruleStatsListQuery,
  useCancelDeviceRequest,
  useSaveInstrumentation,
} from '../api/queries'
import type {
  App,
  DeviceRequest,
  AppInstrumentation as Instrumentation,
  SignalFamily,
} from '../api/types'
import { DeviceData, FAMILY_KEY } from '../components/DeviceData'
import {
  Btn,
  btnClass,
  EmptyNote,
  PageHead,
  ScrollX,
  Section,
  Seg,
  Status,
  useToast,
} from '../components/d'
import { SandboxReadOnly } from '../components/SandboxReadOnly'
import { appLabel, PLATFORM_LABEL } from '../components/Shell'
import { ErrorState, Loading } from '../components/states'
import { useI18n } from '../i18n'
import { compact, day } from '../lib/format'
import { MAX_RULES, TEMPLATES, unusedRules } from '../lib/rules'
import {
  type After,
  BUILTIN_TRIGGERS,
  type Draft,
  draftErrors,
  EVENT_NAME,
  eventName,
  FAMILIES,
  type FamilyDraft,
  LIMITS,
  MAX_OPEN_REQUESTS,
  MODES,
  type Mode,
  type RequestState,
  requestState,
  resourcesSdk,
  retentionParts,
  sameDraft,
  stepRate,
  toBody,
  toDraft,
  toggleTrigger,
} from '../lib/signals'
import { sandboxFor } from '../sandbox'
import { useProjectParams } from './params'
import { conditionText, RuleFlow, TEMPLATE_KEY } from './SignalsRule'
import type { ScopeSearch } from './search'
import { changedLine, useWho } from './useWho'

const MODE_KEY = {
  never: 'signals.mode.never',
  on_trigger: 'signals.mode.onTrigger',
  sampled: 'signals.mode.sampled',
  always: 'signals.mode.always',
} as const satisfies Record<Mode, string>

const WHAT_KEY = {
  summary: 'signals.what.summary',
  errors: 'signals.what.errors',
  logs: 'signals.what.logs',
  spans: 'signals.what.spans',
  network: 'signals.what.network',
  metrics: 'signals.what.metrics',
  replay: 'signals.what.replay',
  resources: 'signals.what.resources',
} as const satisfies Record<SignalFamily, string>

const KIND_KEY = {
  send: 'device.kind.send',
  delete: 'device.kind.delete',
  capture: 'device.kind.capture',
} as const satisfies Record<DeviceRequest['kind'], string>

const TRIGGER_KEY = {
  crash: 'signals.trigger.crash',
  error: 'signals.trigger.error',
  anr: 'signals.trigger.anr',
  rage_tap: 'signals.trigger.rageTap',
  dead_tap: 'signals.trigger.deadTap',
} as const

const STATE = {
  open: { glyph: '○', cls: 'on', key: 'signals.state.open' },
  delivered: { glyph: '✓', cls: 'ok', key: 'signals.state.delivered' },
  expired: { glyph: '–', cls: 'mute', key: 'signals.state.expired' },
  canceled: { glyph: '–', cls: 'mute', key: 'signals.state.canceled' },
  replaced: { glyph: '–', cls: 'mute', key: 'signals.state.replaced' },
} as const satisfies Record<RequestState, { glyph: string; cls: string; key: string }>

/** The choices of the device's limits; a value set elsewhere joins them. */
const AFTERS = ['session', 'window'] as const satisfies readonly After[]
const AFTER_KEY = {
  session: 'signals.after.session',
  window: 'signals.after.window',
} as const satisfies Record<After, string>
const RETENTION_HOURS = [1, 6, 12, 24, 48, 72, 120, 168]
const CAP_MB = [5, 10, 15, 20]
const REPLAY_CAP_MB = [2, 5, 10]
const withValue = (xs: number[], v: number) => [...new Set([...xs, v])].sort((a, b) => a - b)

/** The page, or in the sandbox, which cannot change anything, the reason (D64). */
export function SignalsPage() {
  const { projectId } = useProjectParams()
  return sandboxFor(projectId) ? <SandboxReadOnly /> : <SignalsView />
}

function SignalsView() {
  const { t } = useI18n()
  const { orgId, projectId } = useProjectParams()
  const apps = useQuery(appsQuery(orgId, projectId))
  const org = useQuery(orgQuery(orgId))
  // One app state for the page and the top bar: the scope's app (?app=), or the only app.
  const search = useSearch({ strict: false }) as ScopeSearch
  const list = apps.data ?? []
  const app = list.find((a) => a.id === search.app) ?? (list.length === 1 ? list[0] : undefined)
  // Undefined while the role loads, so an owner never sees the members' note in passing.
  const canEdit = org.data ? org.data.role === 'owner' || org.data.role === 'admin' : undefined
  const title = t('signals.title')

  if (apps.isPending)
    return (
      <main id="main" className="pg cr-pg">
        <PageHead title={title} />
        <Loading />
      </main>
    )
  if (apps.isError)
    return (
      <main id="main" className="pg cr-pg">
        <PageHead title={title} />
        <ErrorState error={apps.error} retry={() => apps.refetch()} />
      </main>
    )
  return (
    <main id="main" className="pg cr-pg">
      {app ? (
        <AppSignals key={app.id} app={app} canEdit={canEdit} orgId={orgId} projectId={projectId} />
      ) : list.length > 1 ? (
        <PickApp apps={list} />
      ) : (
        <>
          <PageHead title={title} />
          <EmptyNote title={t('signals.noApps')}>{t('signals.noAppsBody')}</EmptyNote>
        </>
      )}
      <DeviceRequests orgId={orgId} projectId={projectId} canEdit={canEdit} />
    </main>
  )
}

/**
 * Every app of the project in scope ("All apps"): each app has its own policy, so the page asks
 * for one, setting the same scope as the top bar's app switcher.
 */
function PickApp({ apps }: { apps: readonly App[] }) {
  const { t } = useI18n()
  const { orgId, projectId } = useProjectParams()
  const projects = useQuery(projectsQuery(orgId))
  const project = projects.data?.find((p) => p.id === projectId)
  return (
    <>
      <PageHead
        title={t('signals.title')}
        meta={
          <>
            {project ? <span translate="no">{project.name}</span> : null}
            {project ? ' · ' : null}
            {t('signals.allApps')}
          </>
        }
      />
      <Section title={t('signals.pickApp')} meta={t('signals.pickAppSub')}>
        <div className="cr-pick">
          {apps.map((a) => (
            <Link
              key={a.id}
              className={btnClass()}
              to="."
              search={(prev: Record<string, unknown>) => ({ ...prev, app: a.id })}
            >
              {appLabel(a)}
            </Link>
          ))}
        </div>
      </Section>
    </>
  )
}

function AppSignals({
  app,
  canEdit: role,
  orgId,
  projectId,
}: {
  app: App
  canEdit: boolean | undefined
  orgId: string
  projectId: string
}) {
  const i18n = useI18n()
  const { t } = i18n
  const canEdit = role === true
  const held = useQuery(instrumentationQuery(orgId, projectId, app.id))
  const projects = useQuery(projectsQuery(orgId))
  const project = projects.data?.find((p) => p.id === projectId)
  const who = useWho(orgId)
  const save = useSaveInstrumentation(orgId, projectId, app.id)
  // Changed since it was read (409): reload before publishing again (D117).
  const stale = !!save.error && isApiError(save.error, 409)
  const { toast, show } = useToast()
  const [draft, setDraft] = useState<Draft>()
  // A new policy from the server (a publish, or another tab's) restarts the draft.
  useEffect(() => {
    if (held.data) setDraft(toDraft(held.data))
  }, [held.data])

  const base = held.data ? toDraft(held.data) : undefined
  const dirty = !!draft && !!base && !sameDraft(draft, base)
  const errors = draft ? draftErrors(draft) : []

  function setFamily(name: SignalFamily, next: Partial<FamilyDraft>) {
    setDraft((d) =>
      d ? { ...d, families: { ...d.families, [name]: { ...d.families[name], ...next } } } : d,
    )
  }

  function publish(e: FormEvent) {
    e.preventDefault()
    if (!draft || errors.length > 0) return
    save.mutate(toBody(draft), { onSuccess: () => show(t('signals.published')) })
  }

  const meta = (x: Instrumentation | undefined) => (
    <>
      {project ? <span translate="no">{project.name}</span> : null}
      {project ? ' · ' : null}
      {PLATFORM_LABEL[app.platform]}
      {x
        ? ` · ${
            x.updated_at
              ? changedLine(t, who(x.updated_by), day(i18n, x.updated_at))
              : t('signals.defaults')
          }`
        : null}
    </>
  )

  return (
    <form onSubmit={publish} aria-label={t('signals.title')}>
      <PageHead
        title={t('signals.title')}
        meta={meta(held.data)}
        actions={
          <>
            <Btn
              variant="ghost"
              disabled={!dirty || save.isPending}
              onClick={() => base && setDraft(base)}
            >
              {t('signals.discard')}
            </Btn>
            <Btn
              type="submit"
              variant="solid"
              disabled={!canEdit || !dirty || errors.length > 0 || save.isPending || stale}
              title={stale ? t('signals.reloadFirst') : undefined}
            >
              {t('signals.publish')}
            </Btn>
          </>
        }
      />
      {role === false ? <p className="se-hint cr-note">{t('settings.adminOnly')}</p> : null}
      {stale ? (
        <p role="alert" className="se-hint cr-note cr-stale">
          <Status s="miss" label={t('signals.staleWord')} /> <span>{t('signals.staleSave')}</span>
          <Btn
            variant="ghost"
            size="sm"
            onClick={() => {
              save.reset()
              void held.refetch()
            }}
          >
            {t('signals.reload')}
          </Btn>
        </p>
      ) : save.error ? (
        <p role="alert" className="se-hint cr-note cr-err">
          {isApiError(save.error, 403)
            ? t('settings.adminOnly')
            : isApiError(save.error, 400)
              ? t('signals.invalid')
              : t('state.failed')}
        </p>
      ) : null}
      {held.isPending || !draft ? (
        held.isError ? (
          <ErrorState error={held.error} retry={() => held.refetch()} />
        ) : (
          <Loading />
        )
      ) : (
        <div className="grid2">
          <Section title={t('signals.leaves')} meta={t('signals.leavesSub')}>
            <div className="cr-ctl">
              <div className="cr-cr cr-ch" aria-hidden="true">
                <span>{t('signals.col.signal')}</span>
                <span>{t('signals.col.leaves')}</span>
              </div>
              {FAMILIES.map((name) => (
                <FamilyRow
                  key={name}
                  name={name}
                  f={draft.families[name]}
                  canEdit={canEdit}
                  missingTrigger={errors.some((x) => x.family === name && x.field === 'triggers')}
                  note={
                    name === 'resources'
                      ? resourcesSdk(t, app.platform, PLATFORM_LABEL[app.platform]).line
                      : undefined
                  }
                  unrecorded={
                    name === 'resources' &&
                    resourcesSdk(t, app.platform, PLATFORM_LABEL[app.platform]).since === null
                  }
                  store={draft}
                  onChange={(next) => setFamily(name, next)}
                />
              ))}
            </div>
          </Section>
          <aside className="sections" aria-label={t('signals.device')}>
            <Section title={t('signals.device')} meta={t('signals.deviceSub')}>
              <DeviceLimits
                draft={draft}
                canEdit={canEdit}
                onChange={(next) => setDraft({ ...draft, ...next })}
              />
            </Section>
          </aside>
        </div>
      )}
      {base ? (
        <>
          <RulesSection draft={base} app={app} canEdit={canEdit} />
          <div className="grid2">
            <BuiltIn draft={base} />
            <Templates app={app} />
          </div>
        </>
      ) : null}
      {toast}
    </form>
  )
}

/** The app's rules (D114): the mock's visual list, each row to its page. */
function RulesSection({ draft, app, canEdit }: { draft: Draft; app: App; canEdit: boolean }) {
  const i18n = useI18n()
  const { t } = i18n
  const { orgId, projectId } = useProjectParams()
  const who = useWho(orgId)
  const unused = unusedRules(draft)
  const full = draft.rules.length >= MAX_RULES
  const scope = { app: app.id }
  return (
    <Section
      title={t('rules.section')}
      meta={t('rules.sectionMeta', { n: draft.rules.length })}
      right={
        canEdit ? (
          full ? (
            <span className="cr-hint">{t('rules.limitHint')}</span>
          ) : (
            <Link
              className={btnClass(undefined, 'sm')}
              to="/o/$orgId/p/$projectId/signals/rules/$ruleId"
              params={{ orgId, projectId, ruleId: 'new' }}
              search={scope}
            >
              {`+ ${t('rules.newRule')}`}
            </Link>
          )
        ) : null
      }
    >
      {draft.rules.length === 0 ? (
        <EmptyNote title={t('rules.empty')}>{t('rules.emptyBody')}</EmptyNote>
      ) : (
        <div className="cr-rl">
          <div className="cr-rr cr-rh" aria-hidden="true">
            <span>{t('rules.col.flow')}</span>
            <span>{t('rules.col.rule')}</span>
            <span>{t('rules.col.state')}</span>
            <span>{t('rules.col.sent')}</span>
            <span>{t('rules.col.edited')}</span>
          </div>
          {draft.rules.map((r) => (
            <Link
              key={r.id}
              className="cr-rr"
              to="/o/$orgId/p/$projectId/signals/rules/$ruleId"
              params={{ orgId, projectId, ruleId: r.id }}
              search={scope}
            >
              <RuleFlow />
              <span className="cr-rn">
                <b>{r.name}</b>
                <span translate="no">{`${r.id} · ${r.on}`}</span>
              </span>
              <span className="cr-rs">
                {unused.includes(r.id)
                  ? `– ${t('rules.state.unused')}`
                  : `● ${t('rules.state.inUse')}`}
              </span>
              <RuleSent appId={app.id} ruleId={r.id} />
              <span className="cr-rv2">
                {r.updated_at
                  ? [who(r.updated_by), day(i18n, r.updated_at)].filter(Boolean).join(' · ')
                  : null}
              </span>
            </Link>
          ))}
        </div>
      )}
    </Section>
  )
}

/** A rule's sessions in the last 7 days and when it last sent one; nothing while unknown. */
function RuleSent({ appId, ruleId }: { appId: string; ruleId: string }) {
  const i18n = useI18n()
  const { projectId } = useProjectParams()
  // One call for the whole list: every row reads the same cache entry.
  const list = useQuery(ruleStatsListQuery(projectId, appId))
  const stats = list.data
    ? (list.data.rules.find((r) => r.trigger === `rule:${ruleId}`) ?? {
        sessions: 0,
        last_at: null,
      })
    : undefined
  return (
    <span className="cr-rk">
      {stats ? compact(i18n, stats.sessions) : null}
      {stats?.last_at ? (
        <small>{i18n.t('rules.sentLast', { last: day(i18n, stats.last_at) })}</small>
      ) : null}
    </span>
  )
}

/** The built-in triggers, read-only, with the families that list each. */
function BuiltIn({ draft }: { draft: Draft }) {
  const { t } = useI18n()
  const rows = [...BUILTIN_TRIGGERS, 'event'] as const
  const fires = {
    crash: 'rules.builtin.crash',
    error: 'rules.builtin.error',
    anr: 'rules.builtin.anr',
    rage_tap: 'rules.builtin.rageTap',
    dead_tap: 'rules.builtin.deadTap',
    event: 'rules.builtin.event',
  } as const
  const keptBy = (x: (typeof rows)[number]) =>
    FAMILIES.filter((f) =>
      draft.families[f].triggers.some((tr) =>
        x === 'event' ? eventName(tr) !== undefined : tr === x,
      ),
    )
  return (
    <Section title={t('rules.builtin')} meta={t('rules.builtinSub', { n: rows.length })}>
      <ScrollX label={t('rules.builtin')}>
        <table className="t">
          <thead>
            <tr>
              <th scope="col">{t('rules.col.trigger')}</th>
              <th scope="col">{t('rules.col.fires')}</th>
              <th scope="col">{t('rules.col.keptBy')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((x) => {
              const families = keptBy(x)
              return (
                <tr key={x}>
                  <td>{x === 'event' ? t('signals.trigger.event') : t(TRIGGER_KEY[x])}</td>
                  <td className="wrap">{t(fires[x])}</td>
                  <td className="wrap">
                    {families.length > 0 ? (
                      families.map((f) => t(FAMILY_KEY[f])).join(', ')
                    ) : (
                      <span className="cr-none">–</span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </ScrollX>
    </Section>
  )
}

/** The one-step templates, each to a new rule started from it. */
function Templates({ app }: { app: App }) {
  const { t } = useI18n()
  const { orgId, projectId } = useProjectParams()
  return (
    <Section title={t('rules.templates')} meta={String(TEMPLATES.length)}>
      <ul className="rl cr-tpl">
        {TEMPLATES.map((x) => (
          <li key={x.id}>
            <span>
              <RuleFlow />
              <span>
                <b>{t(TEMPLATE_KEY[x.id])}</b>
                <span className="cr-ts" translate="no">
                  {x.where.map((c) => conditionText(t, c)).join(' · ')}
                </span>
              </span>
            </span>
            <Link
              className={btnClass(undefined, 'sm')}
              to="/o/$orgId/p/$projectId/signals/rules/$ruleId"
              params={{ orgId, projectId, ruleId: 'new' }}
              search={{ app: app.id, template: x.id }}
            >
              {t('rules.use')}
            </Link>
          </li>
        ))}
      </ul>
    </Section>
  )
}

function FamilyRow({
  name,
  f,
  canEdit,
  missingTrigger,
  note,
  unrecorded = false,
  store,
  onChange,
}: {
  name: SignalFamily
  f: FamilyDraft
  canEdit: boolean
  missingTrigger: boolean
  /** What the app's SDK records of the family, when it is not every release (resources). */
  note?: string | undefined
  /** The app's SDK does not record the family yet: its mode cannot change here. */
  unrecorded?: boolean
  store: { retentionHours: number; capMb: number }
  onChange: (next: Partial<FamilyDraft>) => void
}) {
  const { t } = useI18n()
  const locked = name === 'summary'
  const label = t(FAMILY_KEY[name])
  const triggersShown = !locked && (f.mode === 'on_trigger' || f.mode === 'sampled')
  return (
    <div className="cr-cr" data-fam={name}>
      <div className="cr-cn">
        <b>{label}</b>
        <span>{t(WHAT_KEY[name])}</span>
        {locked ? <span className="cr-why">{t('signals.locked')}</span> : null}
        {note ? <span className="cr-why">{note}</span> : null}
      </div>
      <span className="cr-cc">
        <Seg
          label={t('signals.leavesOf', { family: label })}
          options={MODES.map((m) => ({ value: m, label: t(MODE_KEY[m]) }))}
          value={locked ? 'always' : f.mode}
          onChange={(mode) => onChange({ mode })}
          disabled={locked || !canEdit || unrecorded}
        />
        {/* One slot after the segments on every row, so every row's segments end at one edge. */}
        <span className="cr-x">
          {locked ? (
            <span className="cr-lock">{t('signals.lockedWord')}</span>
          ) : f.mode === 'sampled' ? (
            // biome-ignore lint/a11y/useSemanticElements: a stepper, not a form fieldset
            <span
              className="cr-stp"
              role="group"
              aria-label={t('signals.rateOf', { family: label })}
            >
              <button
                type="button"
                aria-label={t('signals.less')}
                disabled={!canEdit || f.ratePct <= 1}
                onClick={() => onChange({ ratePct: stepRate(f.ratePct, -1) })}
              >
                −
              </button>
              <output>{`${f.ratePct} %`}</output>
              <button
                type="button"
                aria-label={t('signals.more')}
                disabled={!canEdit || f.ratePct >= 100}
                onClick={() => onChange({ ratePct: stepRate(f.ratePct, 1) })}
              >
                +
              </button>
            </span>
          ) : null}
        </span>
      </span>
      {triggersShown ? (
        <Triggers
          family={label}
          f={f}
          canEdit={canEdit}
          missing={missingTrigger}
          onChange={onChange}
        />
      ) : null}
      {!locked && f.mode !== 'always' ? (
        <FamilyLimits family={label} f={f} store={store} canEdit={canEdit} onChange={onChange} />
      ) : null}
    </div>
  )
}

/** A family's own retention and cap (spec 15.6), on the row's second line; "as the store" sends neither. */
function FamilyLimits({
  family,
  f,
  store,
  canEdit,
  onChange,
}: {
  family: string
  f: FamilyDraft
  store: { retentionHours: number; capMb: number }
  canEdit: boolean
  onChange: (next: Partial<FamilyDraft>) => void
}) {
  const { t } = useI18n()
  const retention = (h: number) => {
    const r = retentionParts(h)
    return t(r.unit === 'd' ? 'signals.days' : 'signals.hours', { n: r.n })
  }
  const pick = (
    label: string,
    value: number | null,
    options: number[],
    text: (n: number) => string,
    storeText: string,
    set: (n: number | null) => void,
  ) => (
    <label className="cr-fl">
      <span>{label}</span>
      <select
        className="select"
        disabled={!canEdit}
        value={value ?? ''}
        onChange={(e) => set(e.target.value === '' ? null : Number(e.target.value))}
      >
        <option value="">{t('signals.asStore', { value: storeText })}</option>
        {options.map((n) => (
          <option key={n} value={n}>
            {text(n)}
          </option>
        ))}
      </select>
    </label>
  )
  return (
    // biome-ignore lint/a11y/useSemanticElements: two selects of one row, not a form section
    <div className="cr-fls" role="group" aria-label={t('signals.limitsOf', { family })}>
      {pick(
        t('signals.familyRetention'),
        f.retentionHours,
        f.retentionHours === null ? RETENTION_HOURS : withValue(RETENTION_HOURS, f.retentionHours),
        retention,
        retention(store.retentionHours),
        (n) => onChange({ retentionHours: n }),
      )}
      {pick(
        t('signals.familyCap'),
        f.capMb,
        (f.capMb === null ? CAP_MB : withValue(CAP_MB, f.capMb)).filter((n) => n <= store.capMb),
        (n) => t('signals.mb', { n }),
        t('signals.mb', { n: store.capMb }),
        (n) => onChange({ capMb: n }),
      )}
    </div>
  )
}

/** The trigger chips and the window, on the row's second line. */
function Triggers({
  family,
  f,
  canEdit,
  missing,
  onChange,
}: {
  family: string
  f: FamilyDraft
  canEdit: boolean
  missing: boolean
  onChange: (next: Partial<FamilyDraft>) => void
}) {
  const { t } = useI18n()
  const [event, setEvent] = useState('')
  const [adding, setAdding] = useState(false)
  const windowId = useId()
  const toggle = (trigger: string) => onChange(toggleTrigger(f, trigger))
  const events = f.triggers.filter((x) => eventName(x) !== undefined)
  const addEvent = () => {
    const name = event.trim()
    if (!EVENT_NAME.test(name)) return
    const trigger = `event:${name}`
    if (!f.triggers.includes(trigger)) onChange({ triggers: [...f.triggers, trigger] })
    setEvent('')
    setAdding(false)
  }
  return (
    <div className="cr-tg">
      {/* biome-ignore lint/a11y/useSemanticElements: toggle chips, not a fieldset */}
      <span className="cr-chips" role="group" aria-label={t('signals.triggersOf', { family })}>
        <span className="cr-tk">
          {t(f.mode === 'sampled' ? 'signals.outside' : 'signals.triggers')}
        </span>
        {BUILTIN_TRIGGERS.map((x) => (
          <button
            key={x}
            type="button"
            className="cr-chip"
            aria-pressed={f.triggers.includes(x)}
            disabled={!canEdit}
            onClick={() => toggle(x)}
          >
            {t(TRIGGER_KEY[x])}
          </button>
        ))}
        {events.map((x) => (
          <button
            key={x}
            type="button"
            className="cr-chip"
            aria-pressed="true"
            aria-label={t('signals.removeEvent', { name: eventName(x) ?? '' })}
            disabled={!canEdit}
            onClick={() => toggle(x)}
          >
            <span translate="no">{`${t('signals.trigger.event')}: ${eventName(x)}`}</span>
            <span aria-hidden="true"> ×</span>
          </button>
        ))}
        {canEdit ? (
          adding ? (
            <span className="cr-ev">
              <input
                className="input mono"
                aria-label={t('signals.eventName')}
                placeholder="checkout.failed"
                maxLength={64}
                translate="no"
                value={event}
                // biome-ignore lint/a11y/noAutofocus: opened by the person, to type a name
                autoFocus
                onChange={(e) => setEvent(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addEvent()
                  }
                  if (e.key === 'Escape') setAdding(false)
                }}
              />
              <Btn size="sm" disabled={!EVENT_NAME.test(event.trim())} onClick={addEvent}>
                {t('signals.addEvent')}
              </Btn>
            </span>
          ) : (
            <button type="button" className="cr-chip add" onClick={() => setAdding(true)}>
              {`+ ${t('signals.trigger.event')}`}
            </button>
          )
        ) : null}
      </span>
      {f.triggers.length > 0 || f.mode === 'on_trigger' ? (
        <span className="cr-wm">
          <label htmlFor={windowId}>{t('signals.window')}</label>
          <input
            id={windowId}
            className="input mono"
            type="number"
            inputMode="numeric"
            min={LIMITS.windowMinutes.min}
            max={LIMITS.windowMinutes.max}
            disabled={!canEdit}
            value={f.windowMinutes}
            onChange={(e) => onChange({ windowMinutes: Number(e.target.value) })}
          />
          <span>{t('signals.minBefore')}</span>
          <Seg
            label={t('signals.afterOf', { family })}
            options={AFTERS.map((a) => ({ value: a, label: t(AFTER_KEY[a]) }))}
            value={f.after}
            onChange={(after) => onChange({ after })}
            disabled={!canEdit}
          />
        </span>
      ) : null}
      {missing ? (
        <span role="alert" className="cr-miss">
          {t('signals.needTrigger')}
        </span>
      ) : null}
    </div>
  )
}

function DeviceLimits({
  draft,
  canEdit,
  onChange,
}: {
  draft: Draft
  canEdit: boolean
  onChange: (next: Partial<Draft>) => void
}) {
  const { t } = useI18n()
  const retention = (h: number) => {
    const r = retentionParts(h)
    return t(r.unit === 'd' ? 'signals.days' : 'signals.hours', { n: r.n })
  }
  const field = (
    label: string,
    value: number,
    options: number[],
    text: (n: number) => string,
    set: (n: number) => void,
  ) => (
    <label className="lbl">
      {label}
      <select
        className="select"
        disabled={!canEdit}
        value={value}
        onChange={(e) => set(Number(e.target.value))}
      >
        {options.map((n) => (
          <option key={n} value={n}>
            {text(n)}
          </option>
        ))}
      </select>
    </label>
  )
  return (
    <div className="se-form cr-dev">
      {field(
        t('signals.retention'),
        draft.retentionHours,
        withValue(RETENTION_HOURS, draft.retentionHours),
        retention,
        (n) => onChange({ retentionHours: n }),
      )}
      {field(
        t('signals.cap'),
        draft.capMb,
        withValue(CAP_MB, draft.capMb),
        (n) => t('signals.mb', { n }),
        (n) => onChange({ capMb: n, replayCapMb: Math.min(draft.replayCapMb, n) }),
      )}
      {field(
        t('signals.replayCap'),
        draft.replayCapMb,
        withValue(REPLAY_CAP_MB, draft.replayCapMb).filter((n) => n <= draft.capMb),
        (n) => t('signals.mb', { n }),
        (n) => onChange({ replayCapMb: n }),
      )}
      <p className="se-hint">{t('signals.deletion')}</p>
    </div>
  )
}

/** The project's device requests, or with `user` one app user's (the People page, D114). */
export function DeviceRequests({
  orgId,
  projectId,
  canEdit: role,
  user,
}: {
  orgId: string
  projectId: string
  canEdit: boolean | undefined
  user?: string
}) {
  const i18n = useI18n()
  const { t, number } = i18n
  const canEdit = role === true
  const requests = useQuery(deviceRequestsQuery(orgId, projectId, user))
  const cancel = useCancelDeviceRequest(orgId, projectId)
  const who = useWho(orgId)
  const rows = requests.data ?? []
  const open = rows.filter((r) => r.state === 'open').length
  const families = (r: DeviceRequest) =>
    r.families === null
      ? t('signals.allFamilies')
      : r.families.map((f) => t(FAMILY_KEY[f])).join(', ')
  return (
    <section id="requests" className="sec" aria-labelledby="requests-h">
      <div className="sec-h">
        <h2 id="requests-h">{t('signals.requests')}</h2>
        <span className="m">
          {user
            ? t('people.requestsSub', { open })
            : t('signals.requestsSub', { open, max: MAX_OPEN_REQUESTS })}
        </span>
        <span className="r">
          <DeviceData
            orgId={orgId}
            projectId={projectId}
            {...(user ? { user } : {})}
            canEdit={role}
            label={t('signals.newRequest')}
          />
        </span>
      </div>
      {open >= MAX_OPEN_REQUESTS ? (
        <p className="se-hint cr-warn">{t('device.tooMany', { max: MAX_OPEN_REQUESTS })}</p>
      ) : null}
      {requests.isPending ? (
        <Loading />
      ) : requests.isError ? (
        <ErrorState error={requests.error} retry={() => requests.refetch()} />
      ) : rows.length === 0 ? (
        user ? (
          <p className="dim cr-empty">{t('people.noRequests')}</p>
        ) : (
          <EmptyNote title={t('signals.noRequests')}>{t('signals.noRequestsBody')}</EmptyNote>
        )
      ) : (
        <ScrollX label={t('signals.requests')}>
          <table className="t">
            <thead>
              <tr>
                <th scope="col" className="f">
                  {t('signals.col.kind')}
                </th>
                <th scope="col">{t('signals.col.subject')}</th>
                <th scope="col">{t('signals.col.families')}</th>
                <th scope="col">{t('signals.col.by')}</th>
                <th scope="col">{t('signals.col.created')}</th>
                <th scope="col">{t('signals.col.expires')}</th>
                <th scope="col">{t('signals.col.answers')}</th>
                <th scope="col">{t('signals.col.state')}</th>
                <th scope="col" className="last">
                  <span className="visually-hidden">{t('signals.col.actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const s = STATE[requestState(r)]
                return (
                  <tr key={r.id}>
                    <td className="f">{t(KIND_KEY[r.kind])}</td>
                    <td>
                      <span className="dim">
                        {t(r.subject_kind === 'install' ? 'signals.install' : 'signals.user')}
                      </span>{' '}
                      <code translate="no">{`…${r.subject_hint}`}</code>
                    </td>
                    <td>{families(r)}</td>
                    <td>{who(r.created_by) ?? <span className="dim">–</span>}</td>
                    <td>{day(i18n, r.created_at)}</td>
                    <td>{day(i18n, r.expires_at)}</td>
                    <td>
                      {r.answers
                        ? t('signals.answers', {
                            devices: r.answers.devices,
                            records: number(r.answers.records),
                          })
                        : '–'}
                    </td>
                    <td>
                      <span className={`st ${s.cls}`}>
                        <span aria-hidden="true">{`${s.glyph} `}</span>
                        {t(s.key)}
                      </span>
                    </td>
                    <td className="last">
                      {r.state === 'open' ? (
                        <Btn
                          variant="ghost"
                          size="sm"
                          disabled={!canEdit || cancel.isPending}
                          onClick={() => cancel.mutate(r.id)}
                        >
                          {t('signals.cancel')}
                        </Btn>
                      ) : null}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </ScrollX>
      )}
    </section>
  )
}
